import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { first } from 'lodash'
import moment from 'moment'
import classNames from 'classnames'
import { IconUser, IconClock, IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { formatTime } from '@/utils/helpers'
import { CHANNEL_NAME_DISPLAY } from '@/utils/constants'
import { fetchScoreById } from '@/reducers/scorecards/scorecards.actions'
import { ExternalLink } from '@/components/ExternalLink'
import { Pill } from '@/components/pills/Pill'

import { MeasureScoresTable } from '../MeasureScoresTable/MeasureScoresTable'
import { ScoreAccordion } from '../ScoreAccordion/ScoreAccordion'
import { getScoreThreshold } from '../helpers'
import { calculatePercentage } from './helpers'

import './callScoreCarousel.css'

const CallScoreCarouselItem = ({ sectionScore }) => {
  const score = calculatePercentage(sectionScore.aggregate_score, sectionScore.possible_score)

  return (
    <ScoreAccordion score={score} title={sectionScore.name}>
      <MeasureScoresTable measureScores={sectionScore.measure_scores} canViewCriteria />
    </ScoreAccordion>
  )
}

export const CallScoreMetadata = ({ user, call_duration, call_id, channelType }) => {
  const channelName = CHANNEL_NAME_DISPLAY[channelType]

  return (
    <>
      <div className="call-score-carousel__metadata">
        <span data-testid="call-score-carousel-user-name">
          <IconUser className="icon-svg" />
          {user}
        </span>
        <span>
          <IconClock className="icon-svg" />
          {`${call_duration} mins`}
        </span>
      </div>
      <div className="call-score-carousel__metadata" style={{ marginLeft: 'auto' }}>
        <span className="external-link">
          <ExternalLink
            url={`../calls/${call_id}`}
            target="_blank"
            label={`Explore ${channelName}`}
            id={call_id}
          />
        </span>
      </div>
    </>
  )
}

const CallScoreCarouselPage = ({ callScoreId, closeCarousel, numPages, pageNumber }) => {
  const dispatch = useDispatch()
  const { data } = useSelector((state) => state.scorecards)
  const { targetScorecardConfig } = data
  const { currentScore } = data
  // Use appliedCallType from data, which only updates after clicking Filter button
  const channelType = data.appliedCallType
  const channelName = CHANNEL_NAME_DISPLAY[channelType]
  const {
    user_name: userName,
    section_scores: sectionScores,
    aggregate_score: aggregateScore,
    possible_score: possibleScore,
    auto_failed,
    auto_zeroed,
    call_id,
  } = currentScore
  const percentageScore = calculatePercentage(aggregateScore, possibleScore)
  const successClass = getScoreThreshold(percentageScore, targetScorecardConfig)
  const callStartTime = formatTime(currentScore.start_time)
  const startTime = moment(currentScore.start_time)
  const stopTime = moment(currentScore.end_time)
  const diff = stopTime.diff(startTime, 'minutes')

  useEffect(() => {
    dispatch(fetchScoreById(callScoreId))
  }, [dispatch, callScoreId])

  return (
    <div className="call-score-carousel__page" data-testid="call-score-carousel-page">
      <div className="call-score-carousel__title" data-testid="call-score-carousel-header">
        <h3 data-testid="call-score-carousel-title">{callStartTime}</h3>
        <Button small square basic onClick={closeCarousel}>
          <IconX />
        </Button>
      </div>
      <div className="call-score-carousel__content">
        <CallScoreMetadata
          user={userName}
          call_id={call_id}
          call_duration={diff}
          channelType={channelType}
        />
        {!sectionScores && (
          <h3 className="call-score-carousel__empty-scores">There are no scores.</h3>
        )}
        {currentScore &&
          sectionScores &&
          sectionScores
            .sort((a, b) => a.order_id - b.order_id)
            .map((sectionScore) => (
              <CallScoreCarouselItem sectionScore={sectionScore} key={sectionScore.name} />
            ))}
        <span className="call-score-carousel__overall-score-section">
          {auto_failed && (
            <Pill emphasized small critical data-testid="autofail-badge">
              Auto-fail
            </Pill>
          )}
          {auto_zeroed && (
            <Pill emphasized small caution data-testid="autozero-badge">
              Auto-zero
            </Pill>
          )}
          <h3 className="mt">
            <span style={{ marginRight: '.5rem' }}>{channelName} Score:</span>
            <span
              className={classNames({
                'status-success': successClass === 'exceeds_threshold',
                'status-warning': successClass === 'meets_threshold',
                'status-caution': successClass === 'improvement_threshold',
                'status-critical': successClass === 'failure',
              })}
            >
              {calculatePercentage(aggregateScore, possibleScore)}%
            </span>
          </h3>
        </span>
      </div>
      <div className="call-score-carousel__footer" data-testid="call-score-carousel-footer">
        {`${pageNumber} of ${numPages}`}
      </div>
    </div>
  )
}

export const CallScoreCarousel = ({ callScoreIds, closeCarousel }) => {
  const { data } = useSelector((state) => state.scorecards)
  const [scoreId, setScoreId] = useState(first(data.selectedScorecards)?.id)
  const [currentPage, setCurrentPage] = useState(
    callScoreIds.indexOf(first(data.selectedScorecards)?.id)
  )
  useEffect(() => {
    setScoreId(first(data.selectedScorecards)?.id)
  }, [data.selectedScorecards])
  const numPages = callScoreIds.length
  const pageNumber = currentPage + 1
  const previousPage = () => {
    if (currentPage !== 0) {
      setScoreId(callScoreIds[currentPage - 1])
      setCurrentPage(currentPage - 1)
    }
  }

  const nextPage = () => {
    if (currentPage !== callScoreIds.length - 1) {
      setScoreId(callScoreIds[currentPage + 1])
      setCurrentPage(currentPage + 1)
    }
  }

  return (
    <div className="call-score-carousel" data-testid="call-score-carousel">
      <Button
        square
        onClick={previousPage}
        data-testid="previous-page-button"
        aria-label="previous-page-button"
      >
        <IconChevronLeft />
      </Button>
      <CallScoreCarouselPage
        callScoreId={scoreId}
        pageNumber={pageNumber}
        closeCarousel={closeCarousel}
        numPages={numPages}
      />
      <Button
        square
        onClick={nextPage}
        data-testid="next-page-button"
        aria-label="next-page-button"
      >
        <IconChevronRight />
      </Button>
    </div>
  )
}
